import {useFileUploadStoreApi} from '@common/uploads/uploader/file-upload-provider';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {useCallback, useMemo, useRef} from 'react';
import {
  CountdownState,
  formatSpeed,
  OverallEta,
  smoothCountdown,
} from './upload-eta';
import {
  overallUploadEta,
  uploadEtaSnapshot,
  UploadEtaSnapshot,
  useUploadEtaTick,
} from './upload-eta-registry';

/** Speed/ETA of one row, recomputed on the shared 1 s tick only. */
export function useRowEta(
  id: string,
  remainingBytes: number,
  active: boolean,
): UploadEtaSnapshot | null {
  const store = useFileUploadStoreApi();
  const tick = useUploadEtaTick(s => (active ? s.tick : -1));
  const remaining = useRef(remainingBytes);
  remaining.current = remainingBytes;
  const countdown = useRef<CountdownState | null>(null);
  return useMemo(() => {
    if (!active) {
      countdown.current = null;
      return null;
    }
    const row = withCountdown(countdown, uploadEtaSnapshot(id, remaining.current));
    if (row.seconds == null) return row;
    // Files share the connection: when others finish this one speeds up, so
    // it can never take longer than everything together.
    const overall = store
      ? overallUploadEta(store.getState().fileUploads.values()).seconds
      : null;
    return overall != null && overall < row.seconds
      ? {...row, seconds: overall}
      : row;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, active, tick, store]);
}

/** Time left for all running + queued uploads of the current store (1 s tick). */
export function useOverallEta(active: boolean): OverallEta | null {
  const store = useFileUploadStoreApi();
  const tick = useUploadEtaTick(s => (active ? s.tick : -1));
  const countdown = useRef<CountdownState | null>(null);
  return useMemo(() => {
    if (!active) {
      countdown.current = null;
      return null;
    }
    return withCountdown(
      countdown,
      overallUploadEta(store.getState().fileUploads.values()),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, active, tick]);
}

export function useSpeedFormatter() {
  const {localeCode} = useSelectedLocale();
  return useCallback(
    (bytesPerSecond: number) => formatSpeed(bytesPerSecond, localeCode),
    [localeCode],
  );
}

function withCountdown<T extends {seconds: number | null}>(
  ref: {current: CountdownState | null},
  value: T,
): T {
  ref.current = smoothCountdown(ref.current, value.seconds, Date.now());
  return {...value, seconds: ref.current?.seconds ?? null};
}
